import { useCallback, useEffect, useRef, useState } from "react";
import {
  exportHierarchyGeojson,
  listShapeUpdates,
  undoShapeUpdate,
  updateHierarchyShapes,
  type ShapeUpdateRecord,
  type ShapeUpdateResult,
} from "@/lib/api";
import type { HierarchyLevel } from "@/lib/types";
import { Banner, Button, ConfirmDialog, Spinner } from "./ui";
import { useLanguage } from "@/i18n";
import { formatDateTime } from "@/lib/format";

const LEVELS: HierarchyLevel[] = ["zone", "ward", "block"];

/**
 * Correcting zone / ward / block outlines without deleting them.
 *
 * Export a level (with SQL ids) -> edit in GIS -> "Update" with the edited
 * file. The file is checked first (a dry run on the server, nothing written)
 * and the dialog shows exactly what would change; Apply then writes it, and
 * every applied update can be undone from the list below.
 *
 * Kept apart from the import cards above on purpose: those CREATE rows and
 * their undo DELETES them, while everything here edits rows in place.
 */
export default function HierarchyShapeUpdateSection({
  projectId,
  refreshKey,
  onUpdated,
}: {
  projectId: number;
  refreshKey?: unknown;
  /** Fired after an apply or undo so the page reloads lists and the map. */
  onUpdated: () => void;
}) {
  const { t, lang } = useLanguage();
  const ts = t.shapeUpdate;
  const levelLabel: Record<HierarchyLevel, string> = {
    zone: t.mapPreview.boundaryZones,
    ward: t.mapPreview.boundaryWards,
    block: t.mapPreview.boundaryBlocks,
  };

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ level: HierarchyLevel; file: File; result: ShapeUpdateResult } | null>(null);
  const [history, setHistory] = useState<ShapeUpdateRecord[]>([]);
  const [undoing, setUndoing] = useState<ShapeUpdateRecord | null>(null);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const reloadHistory = useCallback(() => {
    listShapeUpdates(projectId)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [projectId]);
  useEffect(reloadHistory, [reloadHistory, refreshKey]);

  async function doExport(level: HierarchyLevel) {
    setBusy(`export-${level}`);
    setError(null);
    try {
      await exportHierarchyGeojson(projectId, level);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function check(level: HierarchyLevel, files: FileList | null) {
    const file = files?.[0];
    const input = inputs.current[level];
    if (input) input.value = "";
    if (!file) return;
    setBusy(`check-${level}`);
    setError(null);
    setNotice(null);
    try {
      const result = await updateHierarchyShapes(projectId, level, file, false);
      setPreview({ level, file, result });
    } catch (err) {
      setError(err instanceof Error ? err.message : ts.failed);
    } finally {
      setBusy(null);
    }
  }

  async function apply() {
    if (!preview) return;
    setBusy("apply");
    setError(null);
    try {
      const result = await updateHierarchyShapes(projectId, preview.level, preview.file, true);
      setNotice(ts.applied(result.changes.length, levelLabel[preview.level]));
      setPreview(null);
      reloadHistory();
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : ts.failed);
      setPreview(null);
    } finally {
      setBusy(null);
    }
  }

  async function undo() {
    if (!undoing) return;
    setBusy("undo");
    setError(null);
    try {
      await undoShapeUpdate(projectId, undoing.id);
      setUndoing(null);
      reloadHistory();
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : ts.failed);
      setUndoing(null);
    } finally {
      setBusy(null);
    }
  }

  const r = preview?.result;
  // Rows with any change (one row can be reshaped AND renamed).
  const changeCount = r?.changes.length ?? 0;

  return (
    <div className="mt-4 border-t border-line pt-3">
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">{ts.title}</p>
      <p className="mt-1 text-2xs text-ink-muted">{ts.intro}</p>

      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {LEVELS.map((level) => (
          <div key={level} className="flex flex-wrap items-center gap-1.5 rounded bg-surface-raised px-2 py-1.5 shadow-ring">
            <input
              ref={(el) => {
                inputs.current[level] = el;
              }}
              type="file"
              accept=".geojson,.json"
              className="hidden"
              onChange={(e) => void check(level, e.target.files)}
            />
            <Button
              size="sm"
              variant="secondary"
              className="h-7 px-2 text-2xs"
              disabled={busy !== null}
              onClick={() => void doExport(level)}
            >
              {busy === `export-${level}` ? <Spinner className="h-3 w-3" /> : null}
              {busy === `export-${level}` ? ts.exporting : ts.exportLevel(levelLabel[level])}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="h-7 px-2 text-2xs"
              disabled={busy !== null}
              onClick={() => inputs.current[level]?.click()}
            >
              {busy === `check-${level}` ? <Spinner className="h-3 w-3" /> : null}
              {busy === `check-${level}` ? ts.checking : ts.updateLevel(levelLabel[level])}
            </Button>
          </div>
        ))}
      </div>

      {error && (
        <div className="mt-2">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}
      {notice && <p className="mt-2 text-2xs font-medium text-ink">{notice}</p>}

      {history.length > 0 && (
        <div className="mt-3">
          <p className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">{ts.historyTitle}</p>
          <ul className="mt-1.5 space-y-1">
            {history.slice(0, 5).map((h) => (
              <li
                key={h.id}
                className="flex items-center justify-between gap-2 rounded bg-surface-raised px-3 py-1.5 text-2xs shadow-ring"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">
                    {levelLabel[h.level]} · {h.filename ?? "—"}
                  </p>
                  <p className="text-ink-muted">
                    {ts.historyRow(h)}
                    {h.created_by ? ` · ${h.created_by}` : ""}
                    {h.created_at ? ` · ${formatDateTime(h.created_at, lang)}` : ""}
                    {h.undone_at ? ` · ${ts.undone}` : ""}
                  </p>
                </div>
                {!h.undone_at && (
                  <Button
                    size="sm"
                    variant="danger"
                    className="h-7 shrink-0 px-2 text-2xs"
                    disabled={busy !== null}
                    onClick={() => setUndoing(h)}
                  >
                    {ts.undo}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The dry-run result. Apply re-sends the same file; the server checks
          it again rather than trusting this preview. */}
      <ConfirmDialog
        open={preview !== null}
        danger={false}
        title={preview ? ts.previewTitle(levelLabel[preview.level].toLowerCase(), preview.file.name) : ""}
        body={r ? (changeCount > 0 ? ts.summary(r) : `${ts.summary(r)}. ${ts.nothingToApply}`) : ""}
        confirmLabel={ts.apply(changeCount)}
        confirmDisabled={changeCount === 0}
        busy={busy === "apply"}
        onCancel={() => setPreview(null)}
        onConfirm={() => void apply()}
      >
        {r && (
          <div className="space-y-2">
            {r.features_moved > 0 && (
              <div>
                <Banner tone="info">{ts.movedInfo(r.features_moved)}</Banner>
                <ul className="thin-scroll mt-1 max-h-28 space-y-0.5 overflow-auto text-2xs text-ink-secondary">
                  {r.moved_features.map((m) => (
                    <li key={m.feature_id}>
                      · {ts.movedRow(m.property_no, m.from_block, m.to_block)}
                      {m.to_submitted && (
                        <span className="ml-1 text-status-text-critical">({ts.toSubmitted})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {r.features_outside > 0 && <Banner tone="warning">{ts.outsideWarning(r.features_outside)}</Banner>}
            {r.changes.length > 0 && (
              <div className="thin-scroll max-h-52 overflow-auto rounded shadow-ring">
                <table className="w-full border-collapse text-left text-2xs">
                  <thead className="sticky top-0 bg-surface text-ink-muted">
                    <tr>
                      <th className="px-2 py-1 font-semibold">{ts.colName}</th>
                      <th className="px-2 py-1 font-semibold">{ts.colMatchedBy}</th>
                      <th className="px-2 py-1 font-semibold">{ts.colChange}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.changes.map((c) => (
                      <tr key={c.row_id} className="border-t border-line">
                        <td className="px-2 py-1 text-ink">
                          {c.name} <span className="text-ink-muted">#{c.row_id}</span>
                        </td>
                        <td className="px-2 py-1 text-ink-muted">{c.matched_by}</td>
                        <td className="px-2 py-1 text-ink-secondary">
                          {[
                            c.reshaped ? ts.changeShape : null,
                            c.renamed_from !== null ? ts.changeName(c.renamed_from) : null,
                            c.moved_to !== null ? ts.changeMove(c.moved_from, c.moved_to) : null,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                          {c.features_outside > 0 && (
                            <span className="ml-1 text-status-text-critical">· {ts.outsideCount(c.features_outside)}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {r.skipped_features.length > 0 && (
              <div>
                <p className="text-2xs font-semibold text-ink-muted">{ts.skippedTitle}</p>
                <ul className="thin-scroll mt-1 max-h-28 space-y-0.5 overflow-auto text-2xs text-ink-muted">
                  {r.skipped_features.map((s) => (
                    <li key={s.feature}>· {ts.skippedRow(s.feature, s.name, s.reason)}</li>
                  ))}
                </ul>
              </div>
            )}
            {(r.changes.length >= 300 || r.skipped_features.length >= 300) && (
              <p className="text-2xs text-ink-muted">{ts.listCapped}</p>
            )}
          </div>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={undoing !== null}
        title={ts.undoTitle}
        body={ts.undoBody}
        confirmLabel={ts.undo}
        busy={busy === "undo"}
        onCancel={() => setUndoing(null)}
        onConfirm={() => void undo()}
      />
    </div>
  );
}
